import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { ListChecks, Plus, Search } from 'lucide-react';
import { FeesApi, FeeAdjustmentGroupRow, OrgFeesApi } from '@/services/fees.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { usePermissions } from '@/hooks/usePermissions';
import { fmtMoney } from './lib/feeFormat';
import { InstallmentsModal, PaymentModal, DiscountModal, InstallmentTarget } from './lib/FeeInstallmentModals';
import { CreateNooraniFeeModal } from './lib/CreateNooraniFeeModal';

const columnHelper = createColumnHelper<FeeAdjustmentGroupRow>();

/**
 * Noorani Fees — same grouped-list screen as Fees Adjustment (DataTable +
 * installment-breakdown modal + Payment/Discount actions), pointed at the
 * `/fees/noorani*` endpoints instead of `/fees/adjustment*`. Those
 * endpoints are a filtered VIEW over the same fee_demands pipeline
 * (fee_structures.name ILIKE '%noorani%') — no separate table/column, no
 * duplicated query or action logic, see fees-noorani.controller.ts.
 *
 * At `scope="organization"` this is reachable across any school in scope
 * via an explicit School field, instead of switching the whole session —
 * see FeesPage.tsx's doc comment for the full rationale.
 */
export function NooraniFeesPage({ scope = 'school' }: { scope?: 'organization' | 'school' }) {
  const term = useTerminology();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canCreate = can('/fees', 'create');
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'studentName', defaultSortOrder: 'asc' });
  const showSchoolPicker = scope === 'organization';

  const [schoolId, setSchoolId] = useState('');
  const [yearId, setYearId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [installmentsGroup, setInstallmentsGroup] = useState<FeeAdjustmentGroupRow | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<InstallmentTarget | null>(null);
  const [discountTarget, setDiscountTarget] = useState<InstallmentTarget | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: schools = [] } = useQuery({
    queryKey: ['org-fees-schools'],
    queryFn: OrgFeesApi.schools,
    enabled: showSchoolPicker,
  });
  const haveTarget = !showSchoolPicker || !!schoolId;

  const { data: defaultYear, isLoading: defaultYearLoading } = useQuery({
    queryKey: ['fees', 'default-year', schoolId],
    queryFn: () => (showSchoolPicker ? OrgFeesApi.defaultYear(schoolId) : FeesApi.defaultYear()),
    enabled: haveTarget,
  });
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-fees-selector', schoolId],
    queryFn: () =>
      showSchoolPicker
        ? OrgFeesApi.academicYears(schoolId)
        : AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
    enabled: haveTarget,
  });
  const years = yearsPage?.items ?? [];
  const effectiveYearId = yearId || defaultYear?.id || '';
  const hasAnyData = !!defaultYear;

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-fees-selector', schoolId],
    queryFn: () => (showSchoolPicker ? OrgFeesApi.classes(schoolId) : AcademicClassesApi.list({ limit: 100 })),
    enabled: haveTarget,
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const listParams = {
    ...dt.params,
    academicYearId: effectiveYearId || undefined,
    classId: classId || undefined,
    sectionId: sectionId || undefined,
  };

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['fees', 'noorani', schoolId, listParams],
    queryFn: () => (showSchoolPicker ? OrgFeesApi.nooraniList(schoolId, listParams) : FeesApi.nooraniList(listParams)),
    enabled: !defaultYearLoading && haveTarget,
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['fees', 'noorani'] });
    qc.invalidateQueries({ queryKey: ['fees', 'report'] });
  };

  // Same reasoning as FeesAdjustmentPage: keep the installments modal open
  // across a payment/discount save, swapping in the refreshed breakdown
  // once the invalidated query actually refetches.
  useEffect(() => {
    if (!installmentsGroup || !pageData) return;
    const fresh = pageData.items.find(
      (g) =>
        g.studentId === installmentsGroup.studentId &&
        g.feeStructureId === installmentsGroup.feeStructureId &&
        g.academicYearId === installmentsGroup.academicYearId,
    );
    if (fresh && fresh !== installmentsGroup) setInstallmentsGroup(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData]);

  const columns = [
    columnHelper.accessor('studentName', { header: 'Student Name' }),
    columnHelper.accessor('className', { header: 'Class' }),
    columnHelper.accessor('feeStructureName', { header: 'Fee' }),
    columnHelper.accessor('academicYearName', { header: 'Session Year' }),
    columnHelper.accessor('actualAmount', { header: 'Actual', cell: (ctx) => fmtMoney(ctx.getValue()) }),
    columnHelper.accessor('paidAmount', { header: 'Paid', cell: (ctx) => fmtMoney(ctx.getValue()) }),
    columnHelper.accessor('balance', {
      header: 'Balance',
      cell: (ctx) => <Badge tone={ctx.getValue() <= 0 ? 'green' : 'amber'}>{fmtMoney(ctx.getValue())}</Badge>,
    }),
    columnHelper.display({
      id: 'installments',
      header: '',
      cell: (ctx) => (
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          title="View installments"
          onClick={() => setInstallmentsGroup(ctx.row.original)}
        >
          <ListChecks className="h-4 w-4" /> {ctx.row.original.installments.length} installment
          {ctx.row.original.installments.length === 1 ? '' : 's'}
        </button>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Noorani Fees"
        description="Noorani fee demands, grouped by student and fee. Record payments or apply discounts against each installment."
        actions={
          canCreate &&
          haveTarget && (
            <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              Create Noorani Fee
            </button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {showSchoolPicker && (
          <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="">— Select a school —</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}
        {haveTarget && (
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search by name or admission #"
                className="pl-9"
              />
            </div>
            <Select className="!w-40" value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(''); }}>
              <option value="">All {term.level}</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
              <option value="">All {term.group}</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            <Select className="!w-44" value={yearId || effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
          </>
        )}
      </div>

      {!haveTarget ? (
        <div className="card p-8 text-center text-slate-500">Select a school to view its Noorani fees.</div>
      ) : !hasAnyData && !defaultYearLoading ? (
        <div className="card p-8 text-center text-slate-500">No fee data yet.</div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No Noorani demands for the selected filters."
          getRowId={(row) => `${row.studentId}:${row.feeStructureId}:${row.academicYearId}`}
        />
      )}

      {installmentsGroup && (
        <InstallmentsModal
          group={installmentsGroup}
          onClose={() => setInstallmentsGroup(null)}
          onPay={(inst) =>
            setPaymentTarget({
              id: inst.id,
              studentName: installmentsGroup.studentName,
              feeStructureName: installmentsGroup.feeStructureName,
              installmentName: inst.installmentName,
              actualAmount: inst.actualAmount,
              balance: inst.balance,
            })
          }
          onDiscount={(inst) =>
            setDiscountTarget({
              id: inst.id,
              studentName: installmentsGroup.studentName,
              feeStructureName: installmentsGroup.feeStructureName,
              installmentName: inst.installmentName,
              actualAmount: inst.actualAmount,
              balance: inst.balance,
            })
          }
        />
      )}

      {paymentTarget && (
        <PaymentModal
          target={paymentTarget}
          onClose={() => setPaymentTarget(null)}
          onSaved={invalidate}
          recordPayment={
            showSchoolPicker
              ? (demandId, payload) => OrgFeesApi.nooraniRecordPayment(schoolId, demandId, payload)
              : FeesApi.nooraniRecordPayment
          }
        />
      )}
      {discountTarget && (
        <DiscountModal
          target={discountTarget}
          onClose={() => setDiscountTarget(null)}
          onSaved={invalidate}
          applyDiscount={
            showSchoolPicker
              ? (demandId, payload) => OrgFeesApi.nooraniApplyDiscount(schoolId, demandId, payload)
              : FeesApi.nooraniApplyDiscount
          }
        />
      )}

      <CreateNooraniFeeModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        schoolId={showSchoolPicker ? schoolId : undefined}
      />
    </>
  );
}
